import * as React from "react";
import {Types}    from "@Common/types";

import "./popups.scss";

import {ComponentLink, Link, makeChain} from "@Common/react/cst2";
import {ModalService3}                  from "@Common/ui-components/modal-service-3/modal-service-3";
import {buildUrlQuery}                  from "@Common/utils/build-url-query";

import {consumeContext} from "@Xpresso/loaders/consume-context";
import {InternalLink}   from "@Xpresso/routes/common/components/internal-link";

import {PopupsService} from "./popups-service";

export const Popups = ({next: rootNext}:{next: Link<JSX.Element>}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("userLevels"))
    .then("service", ({}, next:ComponentLink<typeof PopupsService>) => PopupsService({next}))
    .then("modalState", (
        {service, userLevels},
        next:ComponentLink<typeof ModalService3<Types.Apis.Models.PopupModal>, "rootNext">
    ) => (
        ModalService3({
            xdQaName: "Popups Modal",
            rootNext: next,
            next: ({params: popup}) => ModalContent({
                popup,
                onClose: () => service.dismiss(),
                onNeverShowAgain: () => service.dismiss(true),
                onSeen: () => service.consume(),
                userLevels,
            }),
            onClose: async () => service.dismiss(),
            responsive: {
                "": {width: 888},
                1920: {width: 888 * 1.43},
            },
            top: "10vh",
        })
    ))
    .then(({modalState, service}, next) => <>
        <ModalTrigger currentPopup={service.currentPopup} showModal={modalState.show}/>
        {next()}
    </>)
    .then(...consumeContext("routerHistory"))
    .then(rootNext)
    .run()
);

const ModalTrigger = ({currentPopup, showModal}:{
    currentPopup: Types.Apis.Models.PopupModal,
    showModal:(m:Types.Apis.Models.PopupModal, open?:boolean) => void,
}) =>
{
    const lastPopupRef = React.useRef<Types.Apis.Models.PopupModal>(currentPopup);
    React.useEffect(() =>
    {
        if (currentPopup) {
            lastPopupRef.current = currentPopup;
        }
        showModal(lastPopupRef.current, !!currentPopup);
    }, [currentPopup]);
    return null;
};

const ModalContent = ({onClose: emitClose, onNeverShowAgain: emitNeverShowAgain, onSeen: emitSeen, popup, userLevels}:{
    onClose          : () => void,
    onNeverShowAgain : () => void,
    onSeen           : () => void,
    popup            : Types.Apis.Models.PopupModal,
    userLevels       : Types.Apis.Payloads.Next.Session.UserLevel.Response,
}) => (
    <div className="popup-modal-2sm">
        <PopupMediaDisplay
            onLinkClick={emitSeen}
            media={popup.media}
        />
        {!userLevels?.isGuest && <div className="dismiss-ui">
            <a
                xd-qa-type="link"
                xd-qa-name="Remind me later"
                onClick={emitClose}
            >
                Remind me later
            </a>
            <div className="divider"></div>
            <a
                xd-qa-type="link"
                xd-qa-name="Do not show again"
                onClick={emitNeverShowAgain}
            >
                Do not show me this again
            </a>
        </div>}
    </div>
);

const PopupMediaDisplay = ({media, onLinkClick}:{
    media       : Types.Apis.Models.PopupModalMedia,
    onLinkClick : () => void,
}) =>
{
    switch (media.type) {
        case "image":
            return <PopupImageMediaDisplay onLinkClick={onLinkClick} imageData={media.value}/>
        case "youtube":
            return <PopupYouTubeMediaDisplay videoData={media.value}/>
        default:
            throw new Error(`Unknown popup media type '${(media as any).type}'`);
    }
};

const PopupImageMediaDisplay = ({imageData, onLinkClick: emitLinkClick}:{
    imageData   : Types.Apis.Models.PopupModalImageMedia['value'],
    onLinkClick : () => void,
}) =>
{
    const imgNode = (<img className="modal-img" src={imageData.src} alt="Modal image"/>);

    const hasLink       = !!(imageData.link?.url);
    const linkUrlObj    = hasLink ? new URL(imageData.link.url) : null;
    const linkType      = (
        !hasLink
        ? "none"
        : (
            urlIsSameStorefront(linkUrlObj) && !imageData.link.openInNewWindow
            ? "internal"
            : "external"
        )
    );

    switch (linkType) {
        case "none":
            return (<React.Fragment>{imgNode}</React.Fragment>);
        case "internal":
            return (
                <InternalLink
                    to={linkUrlObj.pathname.replace(getStorefrontBasePathRegex(), "")}
                    onClick={() => {emitLinkClick(); return true;}}
                    className="modal-img-link"
                >
                    {imgNode}
                </InternalLink>
            );
        case "external":
            return (
                <a
                    target={imageData.link.openInNewWindow ? "_blank" : "_self"}
                    href={imageData.link.url}
                    onClick={() => emitLinkClick()}
                    className="modal-img-link"
                >
                    {imgNode}
                </a>
            );
    }
};

const PopupYouTubeMediaDisplay = ({videoData}:{
    videoData: Types.Apis.Models.PopupModalYoutubeMedia['value'],
}) => (
    <iframe
        src={`https://www.youtube.com/embed/${videoData.id}?${buildUrlQuery({
            autoplay    : videoData.autoplay ? 1 : 0,
            mute        : videoData.autoplay ? 1 : 0,
        })}`}
        allow="autoplay"
        className="youtube"
    />
);

const getStorefrontBasePathRegex = () =>
{
    const companySlug = window.location.pathname.slice(process.env.PUBLIC_URL.length).split("/")[1];
    return new RegExp(`^${process.env.PUBLIC_URL}(/${companySlug})?`);
};

const urlIsSameStorefront = (urlObj: URL) =>
{
    const hostMatches       = urlObj.host === window.location.host;
    const basePathMatches   = getStorefrontBasePathRegex().test(urlObj.pathname);
    return hostMatches && basePathMatches;
};

